import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FaMagic } from "react-icons/fa";
import ShiftPill from "./ShiftPill";
import { normalizeShiftType, shiftType as shiftTypeConstants } from "services/shiftsService";

const shiftTypeBadgeClass = (type) => {
	const normalized = normalizeShiftType(type);
	if (normalized === shiftTypeConstants.work) return "shift-card__type-badge--work";
	if (normalized === shiftTypeConstants.rest) return "shift-card__type-badge--break";
	if (normalized === shiftTypeConstants.absence) return "shift-card__type-badge--absence";
	if (normalized === shiftTypeConstants.automatic) return "shift-card__type-badge--automatic";
	return "shift-card__type-badge--default";
};

const getShiftTypeLabel = (type, t) => {
	const normalized = normalizeShiftType(type);
	switch (normalized) {
		case shiftTypeConstants.work:
			return t("shifts.form.typeWork");
		case shiftTypeConstants.absence:
			return t("shifts.form.typeAbsence");
		case shiftTypeConstants.vacation:
			return t("shifts.form.typeVacation");
		case shiftTypeConstants.rest:
			return t("shifts.form.typeRest");
		case shiftTypeConstants.automatic:
			return t("shifts.form.typeAutomatic");
		default:
			return type || "—";
	}
};

/**
 * Tarjeta de turno reutilizable (listado /shifts).
 * Cabecera: nombre + periodos sobre fondo color al 30 % y texto al 100 %.
 */
const ShiftCard = ({
	name,
	periodsLabel,
	dayTypesLabel,
	shiftType,
	color,
	description,
	headerActions,
	onCardClick,
}) => {
	const { t } = useTranslation();
	const normalizedShiftType = normalizeShiftType(shiftType);
	const isAutomatic = normalizedShiftType === shiftTypeConstants.automatic;
	const isClickable = typeof onCardClick === "function";

	const handleKeyDown = (event) => {
		if (!isClickable) return;
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			onCardClick();
		}
	};

	return (
		<article
			className={`shift-card card${
				isClickable ? " shift-card--clickable" : ""
			}`}
			onClick={isClickable ? onCardClick : undefined}
			onKeyDown={handleKeyDown}
			role={isClickable ? "button" : undefined}
			tabIndex={isClickable ? 0 : undefined}
		>
			<header className="shift-card__header">
				<ShiftPill name={name} color={color} periodsLabel={periodsLabel} />
				{headerActions ? (
					<div
						className="shift-card__header-actions"
						onClick={(event) => event.stopPropagation()}
					>
						{headerActions}
					</div>
				) : null}
			</header>

			{description ? (
				<p className="shift-card__description">{description}</p>
			) : null}

			<div className="shift-card__body">
				<div className="shift-card__field">
					<span className="shift-card__label">{t("shifts.card.dayTypes")}</span>
					<p className="shift-card__value shift-card__value--days">
						{dayTypesLabel}
					</p>
				</div>
				<div className="shift-card__field">
					<span className="shift-card__label">{t("shifts.card.shiftType")}</span>
					<div>
						<span
							className={`shift-card__type-badge ${shiftTypeBadgeClass(
								shiftType
							)}`}
						>
						{isAutomatic && (
							<FaMagic className="shift-card__type-badge-icon" />
						)}
							{getShiftTypeLabel(shiftType, t)}
						</span>
					</div>
				</div>
			</div>
		</article>
	);
};

ShiftCard.propTypes = {
	name: PropTypes.string.isRequired,
	periodsLabel: PropTypes.string,
	dayTypesLabel: PropTypes.string.isRequired,
	shiftType: PropTypes.string.isRequired,
	color: PropTypes.string,
	description: PropTypes.string,
	headerActions: PropTypes.node,
	onCardClick: PropTypes.func,
};

ShiftCard.defaultProps = {
	periodsLabel: "",
	color: "",
	description: "",
	headerActions: null,
	onCardClick: null,
};

export default ShiftCard;
